<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title">
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McButtonBack href="/report" :query="{ domain: domain }" />
            <DownloadReportDropdown
              :report="report"
              :items="itemDownloadDropdown"
              :actualTotal="totalRows"
            />
          </div>
        </template>
      </PageHeader>
    </div>
    <div class="d-flex align-items-center gap-2 me-2 absolute-right">
      <FavoriteReportButton
        v-if="hasAccess(['favorite-reports:update']) && !report.isSaved"
        :report="report"
      />
      <ModalSavedReport
        v-if="hasAccess(['saved-reports:read']) && !report.isSaved"
        :filter="filterValue"
        :detail="filterDetail"
        :report="report"
      />
      <McButton
        variant="secondary"
        :text="$t('report.title.reportParameter')"
        icon="icon-outline_filter"
        @click="showModal"
      />
    </div>
    <b-card :key="summaryKey" class="mb-2 mc-general__label" style="margin-top: 80px">
      <h5 class="mc-general__label">
        {{ $t('report.fields.dataSummary') }}
      </h5>
      <b-row>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="periodReport_label"
            :label="$t('report.object.reportPeriod')"
            label-for="reportPeriod"
          >
            <span v-if="!isBusy">
              {{ periodeLabel }}
            </span>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="licensePlate_label"
            :label="$t('report.fields.licensePlate')"
            label-for="licensePlate"
          >
            <div v-if="!isBusy" class="d-flex align-items-center gap-2">
              <span>{{ licensePlateLabel }}</span>
              <McBadge
                v-if="vehicleIdCount > 2"
                :text="`+${vehicleIdCount - 2}`"
                variant="success"
                size="small"
                type="pill"
              />
            </div>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
    <McGrapqlTable
      v-if="Object.keys(filters).length > 0"
      :key="tableKey"
      bordered
      withCard
      searchable
      :fields="fields"
      :apiClient="apiClient"
      :hasCheckbox="false"
      :lazyLoadAddress="false"
      :singleSort="true"
      reportKey="fleet-utilization-ijp"
    >
      <template #viewManager="{ handleSetDataFields, dataColumns }">
        <TableViewManager
          :defaultColumnOrder="fields"
          :currentColumnOrder="dataColumns"
          @setColumn="handleSetDataFields"
          @setColumnReport="handleColumnReport"
        />
      </template>
      <template #cell(tripMileage)="data">
        {{ data.item.tripMileage ?? '-' }}
      </template>
      <template #cell(ritageMileage)="data">
        {{ data.item.ritageMileage ?? '-' }}
      </template>
      <template #cell(tripDuration)="data">
        {{ data.item.tripDuration ?? '-' }}
      </template>
      <template #cell(ritageDuration)="data">
        {{ data.item.ritageDuration ?? '-' }}
      </template>
      <template #cell(ritageMileageZoneOne)="data">
        {{ data.item.ritageMileageZone.zone1 ?? '-' }}
      </template>
      <template #cell(ritageMileageZoneTwo)="data">
        {{ data.item.ritageMileageZone.zone2 ?? '-' }}
      </template>
      <template #cell(ritageMileageZoneThree)="data">
        {{ data.item.ritageMileageZone.zone3 ?? '-' }}
      </template>
      <template #cell(ritageMileageZoneFour)="data">
        {{ data.item.ritageMileageZone.zone4 ?? '-' }}
      </template>
      <template #cell(ritageMileageZoneFive)="data">
        {{ data.item.ritageMileageZone.zone5 ?? '-' }}
      </template>
      <template #cell(ritageMileageZoneSix)="data">
        {{ data.item.ritageMileageZone.zone6 ?? '-' }}
      </template>
      <template #cell(ritageMileageZoneTotal)="data">
        {{ data.item.ritageMileageZone.total ?? '-' }}
      </template>
      <template #cell(fuelAdded)="data">
        {{ data.item.fuelAdded ?? '-' }}
      </template>
      <template #cell(fuelRatioPerKm)="data">
        {{ data.item.fuelRatioPerKm ?? '-' }}
      </template>
      <template #cell(fuelRatioPerHour)="data">
        {{ data.item.fuelRatioPerHour ?? '-' }}
      </template>
      <template #cell(ritageDurationZoneOne)="data">
        {{ data.item.ritageDurationZone.zone1 ?? '-' }}
      </template>
      <template #cell(ritageDurationZoneTwo)="data">
        {{ data.item.ritageDurationZone.zone2 ?? '-' }}
      </template>
      <template #cell(ritageDurationZoneThree)="data">
        {{ data.item.ritageDurationZone.zone3 ?? '-' }}
      </template>
      <template #cell(ritageDurationZoneFour)="data">
        {{ data.item.ritageDurationZone.zone4 ?? '-' }}
      </template>
      <template #cell(ritageDurationZoneFive)="data">
        {{ data.item.ritageDurationZone.zone5 ?? '-' }}
      </template>
      <template #cell(ritageDurationZoneSix)="data">
        {{ data.item.ritageDurationZone.zone6 ?? '-' }}
      </template>
      <template #cell(ritageDurationZoneSeven)="data">
        {{ data.item.ritageDurationZone.zone7 ?? '-' }}
      </template>
      <template #cell(ritageDurationZoneTotal)="data">
        {{ data.item.ritageDurationZone.total ?? '-' }}
      </template>
      <template #cell(overSpeed)="data">
        {{ data.item.safety.overspeed ?? '-' }}
      </template>
      <template #cell(totalOverSpeed)="data">
        {{ data.item.safety.maximumOverspeed ?? '-' }}
      </template>
      <template #cell(harshBreaking)="data">
        {{ data.item.safety.harshBraking ?? '-' }}
      </template>
      <template #cell(harshAcceleration)="data">
        {{ data.item.safety.harshAccels ?? '-' }}
      </template>
      <template #cell(harshCornering)="data">
        {{ data.item.safety.harshCornering ?? '-' }}
      </template>
    </McGrapqlTable>
    <ModalFilter
      v-if="isMounted"
      ref="modal-filter"
      :report="report"
      :unRedirect="true"
      @refresh-table="forceRerender"
    />
    <McReportDownloader />
    <ModalSavedReportVerification
      :key="modalVerifReport"
      :domain="domain"
      :message="message"
      :error="isError"
    />
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import McBadge from '@/components/McBadge'
import McButton from '@/components/McButton'
import McButtonBack from '@/components/McButtonBack'
import McGrapqlTable from '@/components/McGrapqlTable'
import McReportDownloader from '@/components/McReportDownloader'
import PageHeader from '@/components/page-header-v2'
import { Helper } from '@/mixin/helper'
import { MixPanel } from '@/mixin/mixpanel'
import { QueryCache } from '@/mixin/query-cache'
import responsive from '@/mixin/responsive'
import Layout from '@/router/layouts/main'
import FavoriteReportButton from '@/router/views/report/components/FavoriteReportButton'
import ModalFilter from '@/router/views/report/components/ModalFilter'
import ModalSavedReport from '@/router/views/report/components/ModalSavedReport'
import ModalSavedReportVerification from '@/router/views/report/components/ModalSavedReportVerification'
import {
  EXPORT_REPORT_FLEET_UTILIZATION_IJP,
  GET_REPORT_FLEET_UTILIZATION_IJP,
} from '@/services/api/report/report'
import moment from 'moment'
import { mapGetters } from 'vuex'
import DownloadReportDropdown from '../../components/DownloadReportDropdown.vue'
import TableViewManager from '../../components/TableViewManager.vue'
import reportList from '../../lib/reportList.js'
import getFields from './lib/fields/fields'
moment.locale('id')

export default {
  page: {
    title: 'Report',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McGrapqlTable,
    TableViewManager,
    McReportDownloader,
    McButton,
    ModalFilter,
    McBadge,
    ModalSavedReport,
    ModalSavedReportVerification,
    FavoriteReportButton,
    DownloadReportDropdown,
  },
  mixins: [Helper, MixPanel, responsive],
  data() {
    return {
      isMounted: false,
      excelIcon: require('@/assets/images/download-format/excel.png'),
      pdfIcon: require('@/assets/images/download-format/pdf.png'),
      totalRows: 1,
      filters: {},
      tableKey: 0,
      isBusy: false,
      reportFields: getFields(this).map((item, i) => ({ ...item, order: i + 1 })),
      reportKey: 'fleet-utilization-ijp',
      filterDetail: {},
      summaryKey: 50,
      queryCache: new QueryCache(),
      message: '',
      isError: false,
      modalVerifReport: 100,
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    domain() {
      return this.report.domain
    },
    title() {
      return this.report.labelName ?? this.$t(this.report.label)
    },
    fields() {
      return this.reportFields
    },
    columns() {
      return this.reportFields
        .filter((item) => item.shown || item.shown === undefined)
        .map((field) => field.header)
    },
    generateFilter() {
      const { valid, msg } = this.validateFilter(this.report.filter, this.filters)
      if (valid) {
        const filters = {}
        Object.keys(this.filters).forEach((item) => {
          if (
            this.filters[item] !== null &&
            ((Array.isArray(this.filters[item]) && this.filters[item].length > 0) ||
              (!Array.isArray(this.filters[item]) && this.filters[item]))
          ) {
            filters[item] = this.filters[item]
          }
        })
        const { periode, vehicleId, ...otherFilter } = filters
        const startDate =
          periode.length > 0 ? moment(periode[0] + ' 00:00:00.000').toISOString() : null
        const endDate =
          periode.length > 0 ? moment(periode[1] + ' 23:59:59.000').toISOString() : null

        const model = {
          startDate,
          endDate,
          vehicleIds: vehicleId,
          ...otherFilter,
        }
        return { valid: true, model }
      } else {
        this.$mcToast.error(msg)
        return { valid: false, model: {} }
      }
    },
    apiClient() {
      return {
        get: async (filter) => {
          const { valid, model } = this.generateFilter
          if (!valid) return {}
          const self = this
          self.isBusy = true
          const originalFilter = filter
          const res = await this.queryCache.fetch(
            GET_REPORT_FLEET_UTILIZATION_IJP,
            {
              filter: {
                ...model,
                column: this.columns,
                ...originalFilter,
              },
            },
            self.reportKey
          )
          const result = res.reportFleetUtilizationIjp.reportFleetUtilizationIjp
          self.totalRows = result?.pagination?.total ?? 0
          self.isBusy = false
          self.message = res.reportFleetUtilizationIjp.message
          self.isError = !res.reportFleetUtilizationIjp.isSuccessful
          self.modalVerifReport += 1
          return result
        },
      }
    },
    periodeLabel() {
      return this.filters.periode
        ? this.momentDateShortMonth(this.filters.periode[0]) +
            ' - ' +
            this.momentDateShortMonth(this.filters.periode[1])
        : '-'
    },

    licensePlateLabel() {
      if (this.filters.vehicleId && this.filters.vehicleId.length > 0) {
        return this.filterDetail != {} ? this.filterDetail.licensePlate : '-'
      } else {
        return '-'
      }
    },
    vehicleIdCount() {
      return this.filters.vehicleId && this.filters.vehicleId.length > 0
        ? this.filters.vehicleId.length
        : 0
    },
    savedReport() {
      return JSON.parse(localStorage.getItem('saveReports'))
    },
    report() {
      const reports = [...reportList].concat(this.savedReport)
      return reports.find((item) => item.key == this.reportKey)
    },
    filterValue() {
      return this.$store.state.report.filterValue
    },
    itemDownloadDropdown() {
      return [
        {
          name: 'excel',
          label: this.$t('global.action.downloadExcel'),
          onClick: () => {
            this.downloadReport('xlsx')
          },
          icon: this.excelIcon,
        },
        {
          name: 'pdf',
          label: this.$t('global.action.downloadPdf'),
          onClick: () => {
            this.downloadReport('pdf')
          },
          icon: this.pdfIcon,
        },
      ]
    },
  },
  watch: {
    '$i18n.locale': {
      handler() {
        this.reportFields = getFields(this)
        this.tableKey += 1
        this.$nextTick(() => {
          if (this.filters.periode) {
            const currentPeriode = [...this.filters.periode]
            this.filters.periode = []
            this.$nextTick(() => {
              this.filters.periode = currentPeriode
            })
          }
        })
      },
      immediate: true,
    },
  },
  mounted() {
    this.reportKey =
      this.$route.query.key && this.savedReport.find((item) => item.key === this.$route.query.key)
        ? this.$route.query.key
        : this.reportKey
    const { reportFilter, reportFilterValue } = this.getFilterReport(this.reportKey)
    this.$store.commit('report/updateFilter', reportFilter)
    this.$store.commit('report/updateFilterValue', reportFilterValue)
    this.filters = { ...this.$store.state.report.filterReport }
    this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
    this.isMounted = true
    this.track('View ' + this.report.event)
    if (!this.mediaQuery.matches) {
      this.$store.dispatch('layout/toggleMenu', true)
    }
  },
  methods: {
    handleColumnReport(fields) {
      this.reportFields = fields
      this.tableKey += 1
      this.summaryKey += 1
    },
    forceRerender() {
      this.filters = { ...this.$store.state.report.filterReport }
      this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
      this.tableKey += 1
      this.summaryKey += 1
    },
    showModal() {
      this.$refs['modal-filter'].SetFormValues(this.filterValue)
      this.$bvModal.show(this.report.modal)
    },
    showDownloader(periode, extension, result) {
      const downloadProgress = {
        status: 'I',
        url: null,
        name:
          `${this.title} ` +
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY') +
          `.${extension}`,
        periode:
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY'),
        downloadedAt: moment().format('DD MMM YYYY HH:mm:ss'),
        type: extension,
        id: result.exportReportFleetUtilizationIjp.id,
      }
      if (result.isSuccessful) {
        this.$store.commit('report/doneProgress', downloadProgress)
      } else {
        downloadProgress.status = 'F'
        this.$store.commit('report/failProgress', downloadProgress)
      }
      this.$store.commit('report/showDownloadPopup', true)
    },
    downloadReport(extension) {
      this.track('Download ' + this.report.event + extension == 'pdf' ? ' - PDF' : ' - Excel')

      const { valid, model } = this.generateFilter
      if (!valid) {
        setTimeout(() => {
          this.showModal()
        }, 1000)
        return false
      }
      const { periode } = this.filters
      let filterVar = {
        ...model,
        column: this.columns,
        downloadAs: extension == 'pdf' ? 'pdf' : 'excel',
      }
      if (this.report.labelName) {
        filterVar.title = this.report.labelName
      }

      this.$apollo
        .query({
          query: EXPORT_REPORT_FLEET_UTILIZATION_IJP,
          variables: {
            filter: filterVar,
          },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          this.showDownloader(periode, extension, res.data.reportFleetUtilizationIjpBackground)
        })
    },
  },
}
</script>
